import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, MessageSquarePlus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Action, Cell, DataTable, Panel, PanelHeader, Row, StatusBadge } from "./ui";
import { ConversationModal, ScheduleModal, formatDay, useConversationOptions } from "./Conversations";

const TONES = { active: "active", pending: "pending", alert: "alert", neutral: "neutral" };

// Filtro "Conselheiro" usado pela Administração e Diretoria (conselheiro vê apenas a própria carteira).
// A lista segue a ala selecionada; se o conselheiro escolhido sair da lista, volta para "Todos os conselheiros".
export const CounselorFilter = ({ value, onChange, ward, testId = "conversations-counselor-filter" }) => {
  const options = useConversationOptions(ward);
  const counselors = options?.counselors || [];
  useEffect(() => {
    if (!options || !value) return;
    if (!counselors.some((c) => c.id === value)) onChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value, ward]);
  if (!options?.can_select_counselor) return null;
  return (
    <select
      data-testid={testId}
      aria-label="Conselheiro"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 rounded-xl border border-border bg-white px-3 text-[14px]"
    >
      <option value="">Todos os conselheiros</option>
      {counselors.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </select>
  );
};

// Acompanhamento por paciente: última conversa realizada, próxima agendada, dias e status.
export const ConversationsOverviewPanel = ({ ward, counselorId }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/portal/conversations/overview", {
        params: { ward, counselor_id: counselorId || undefined },
      });
      setData(payload);
    } catch {
      setData({ items: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward, counselorId]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.items || [];

  return (
    <Panel testId="conversations-overview-panel" className="space-y-4">
      <PanelHeader
        label="Conversas com Conselheiros"
        title="Acompanhamento por paciente"
        hint={
          data
            ? `${rows.length} paciente(s) ativo(s) · ${data.alert_count} há ${data.alert_days} dias ou mais sem conversa realizada.`
            : "Carregando…"
        }
      />
      {rows.length === 0 ? (
        <p data-testid="conversations-overview-empty" className="text-[14px] text-ink-500">
          Nenhum resultado encontrado para os filtros selecionados.
        </p>
      ) : (
        <DataTable
          testId="conversations-overview-table"
          columns={[
            { key: "patient", label: "Paciente" },
            { key: "counselor", label: "Conselheiro" },
            { key: "last", label: "Última conversa" },
            { key: "next", label: "Próxima agendada" },
            { key: "days", label: "Dias" },
            { key: "status", label: "Status" },
            { key: "actions", label: "Ações" },
          ]}
        >
          {rows.map((row) => (
            <Row key={row.patient_id} testId={`conversations-overview-${row.patient_id}`}>
              <Cell>
                <Link to={`/portal/pacientes/${row.patient_id}`} className="font-semibold text-ink-900 hover:underline">
                  {row.patient_name}
                </Link>
              </Cell>
              <Cell>{row.counselor_name || "Não definido"}</Cell>
              <Cell>
                {formatDay(row.last_conversation_date)}
                {row.last_conversation_time ? ` · ${row.last_conversation_time}` : ""}
              </Cell>
              <Cell>
                {row.next_schedule
                  ? `${formatDay(row.next_schedule.scheduled_date)} · ${row.next_schedule.scheduled_time}`
                  : "—"}
              </Cell>
              <Cell className={row.alert ? "font-semibold text-danger" : ""}>
                {row.days_since === null ? "—" : row.days_since}
              </Cell>
              <Cell>
                <StatusBadge tone={TONES[row.status_tone]} testId={`conversations-overview-status-${row.patient_id}`}>
                  {row.status_label}
                </StatusBadge>
              </Cell>
              <Cell>
                <div className="flex flex-wrap gap-2">
                  <Action
                    size="sm"
                    icon={MessageSquarePlus}
                    data-testid={`conversations-overview-register-${row.patient_id}`}
                    onClick={() => setModal({ type: "conversation", row })}
                  >
                    Registrar
                  </Action>
                  <Action
                    size="sm"
                    variant="ghost"
                    icon={CalendarClock}
                    data-testid={`conversations-overview-schedule-${row.patient_id}`}
                    onClick={() => setModal({ type: "schedule", row })}
                  >
                    Agendar
                  </Action>
                </div>
              </Cell>
            </Row>
          ))}
        </DataTable>
      )}

      {modal?.type === "conversation" ? (
        <ConversationModal
          patient={{ id: modal.row.patient_id, full_name: modal.row.patient_name, counselor_id: modal.row.counselor_id }}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      ) : null}
      {modal?.type === "schedule" ? (
        <ScheduleModal
          patient={{ id: modal.row.patient_id, full_name: modal.row.patient_name, counselor_id: modal.row.counselor_id }}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      ) : null}
    </Panel>
  );
};
